import { useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { INPUT_DATE_FORMAT, STANDARD_DATE_FORMAT } from '../../../constants/dateConfig';
import backend from '../../../lib/backend';
import { UpdateCommonAppData } from '../../../lib/backend/requests/updateCommonApplicationProfile';
import commonApplicationProfileForm from '../../../state/forms/eslForms/commonApplicationProfileForm';
import { CommonApplicationProfile, EnrollmentStatus } from '../../../types/commonApplicationProfile';
import { ValidationError } from '../../../types/errors/validation';
import { SponsorshipOpportunityType } from '../../../types/sponsorshipOpportunity';

import { format, isMatch, parse } from 'date-fns';

// Base required fields for all opportunities
const BASE_REQUIRED_FIELDS: string[] = [
  'addressLine',
  'addressDetail',
  'city',
  'state',
  'zip',
  'citizenshipStatus',
];

const ADDITIONAL_REQUIRED_FIELDS: string[] = [
  'expectedGraduationDate',
  'degreeType',
  'major',
  'institution',
  'current_gpa',
];

// Get the required fields for the opportunity based on the type and enrollment status
const getRequiredFields = (opportunityType: SponsorshipOpportunityType, enrollmentStatus?: EnrollmentStatus | null): string[] => {
  // Attract and Keep
  if ([ SponsorshipOpportunityType.ATTRACT, SponsorshipOpportunityType.KEEP ].includes(opportunityType)) {
    return [
      ...BASE_REQUIRED_FIELDS,
      ...ADDITIONAL_REQUIRED_FIELDS,
    ];
  }

  if (opportunityType === SponsorshipOpportunityType.UPSKILL) {
    let additionalFields: string[] = [];

    if (enrollmentStatus === EnrollmentStatus.ENROLLED) {
      additionalFields = ['expectedGraduationDate'];
    } else if (enrollmentStatus === EnrollmentStatus.NOT_ENROLLED) {
      additionalFields = ['expectedEnrollmentDate'];
    }

    return [ ...BASE_REQUIRED_FIELDS, ...additionalFields ];
  }

  console.error(`Invalid opportunity type: ${opportunityType}`);

  return [];
};

const checkAndParseDate = (date: string, inputFormat: string, outputFormat: string): string => {
  if (isMatch(date, outputFormat)) {
    return date;
  }

  try {
    return format(parse(date, inputFormat, new Date()), outputFormat);
  } catch (error) {
    console.error('Date parsing error:', error);

    return date;
  }
};

type UseCommonApplicationFormState = {
  submitForm: () => Promise<CommonApplicationProfile | null>;
  setFieldsFromProfile: (profile: CommonApplicationProfile) => void;
};

const useCommonApplicationForm = (opportunityType: SponsorshipOpportunityType, enrollmentStatus?: EnrollmentStatus | null): UseCommonApplicationFormState => {
  const expectedGraduationDateValid = useRecoilValue(commonApplicationProfileForm.fields.expectedGraduationDate.validState);
  const expectedEnrollmentDateValid = useRecoilValue(commonApplicationProfileForm.fields.expectedEnrollmentDate.validState);
  const degreeTypeValid = useRecoilValue(commonApplicationProfileForm.fields.degreeType.validState);
  const majorValid = useRecoilValue(commonApplicationProfileForm.fields.major.validState);
  const institutionValid = useRecoilValue(commonApplicationProfileForm.fields.institution.validState);
  const gpaValid = useRecoilValue(commonApplicationProfileForm.fields.current_gpa.validState);
  const addressLineValid = useRecoilValue(commonApplicationProfileForm.fields.address_line.validState);
  const addressDetailValid = useRecoilValue(commonApplicationProfileForm.fields.address_detail.validState);
  const cityValid = useRecoilValue(commonApplicationProfileForm.fields.city.validState);
  const stateValid = useRecoilValue(commonApplicationProfileForm.fields.state.validState);
  const zipValid = useRecoilValue(commonApplicationProfileForm.fields.zip.validState);
  const citizenshipStatusValid = useRecoilValue(commonApplicationProfileForm.fields.usCitizenshipStatus.validState);

  // Get field valid states
  const formValid = useMemo(() => {
    const fieldValidMap: Record<string, boolean> = {
      expectedGraduationDate: expectedGraduationDateValid,
      expectedEnrollmentDate: expectedEnrollmentDateValid,
      citizenshipStatus: citizenshipStatusValid,
      degreeType: degreeTypeValid,
      major: majorValid,
      institution: institutionValid,
      current_gpa: gpaValid,
      addressLine: addressLineValid,
      addressDetail: addressDetailValid,
      city: cityValid,
      state: stateValid,
      zip: zipValid,
    };
    const requiredFields = getRequiredFields(opportunityType, enrollmentStatus);

    return requiredFields.every((field) => fieldValidMap[field]);
  }, [ opportunityType, enrollmentStatus, citizenshipStatusValid, expectedGraduationDateValid, expectedEnrollmentDateValid, degreeTypeValid, majorValid, institutionValid, gpaValid, addressLineValid, addressDetailValid, cityValid, stateValid, zipValid ]);

  // Get field values
  const [ expectedGraduationDate, setExpectedGraduationDate ] = useRecoilState(commonApplicationProfileForm.fields.expectedGraduationDate.valueState);
  const [ expectedEnrollmentDate, setExpectedEnrollmentDate ] = useRecoilState(commonApplicationProfileForm.fields.expectedEnrollmentDate.valueState);
  const [ degreeType, setDegreeType ] = useRecoilState(commonApplicationProfileForm.fields.degreeType.valueState);
  const [ major, setMajor ] = useRecoilState(commonApplicationProfileForm.fields.major.valueState);
  const [ institution, setInstitution ] = useRecoilState(commonApplicationProfileForm.fields.institution.valueState);
  const [ gpa, setGpa ] = useRecoilState(commonApplicationProfileForm.fields.current_gpa.valueState);
  const [ addressLine, setAddressLine ] = useRecoilState(commonApplicationProfileForm.fields.address_line.valueState);
  const [ addressDetail, setAddressDetails ] = useRecoilState(commonApplicationProfileForm.fields.address_detail.valueState);
  const [ city, setCity ] = useRecoilState(commonApplicationProfileForm.fields.city.valueState);
  const [ state, setState ] = useRecoilState(commonApplicationProfileForm.fields.state.valueState);
  const [ zip, setZip ] = useRecoilState(commonApplicationProfileForm.fields.zip.valueState);
  const [ citizenshipStatus, setCitizenshipStatus ] = useRecoilState(commonApplicationProfileForm.fields.usCitizenshipStatus.valueState);

  const submitForm = useCallback(async () => {
    if (!formValid) throw new ValidationError();

    const updateCommonAppData: UpdateCommonAppData = {
      us_citizenship_status: citizenshipStatus ? citizenshipStatus.code : null,
      // TODO: Normalize the references to degree type/level throughout the codebase
      // NOTE: degree_level is the same as the serialized degree_type on the common app profile
      degree_level: degreeType,
      major: major?.code,
      institution: institution?.id,
      gpa: gpa,
      address_line: addressLine?.trim(),
      address_detail: addressDetail?.trim(),
      city: city?.trim(),
      state: state?.abbreviation as string,
      zip: zip?.trim(),
    };

    if (expectedEnrollmentDate) {
      updateCommonAppData['expected_enrollment_date'] = checkAndParseDate(expectedEnrollmentDate, INPUT_DATE_FORMAT, STANDARD_DATE_FORMAT);
    }

    if (expectedGraduationDate) {
      updateCommonAppData['expected_graduation_date'] = checkAndParseDate(expectedGraduationDate, INPUT_DATE_FORMAT, STANDARD_DATE_FORMAT);
    }

    const { data: commonApplicationProfile } = await backend.updateCommonApplicationProfile(updateCommonAppData);

    return commonApplicationProfile;
  }, [
    expectedEnrollmentDate,
    expectedGraduationDate,
    degreeType,
    major,
    institution,
    gpa,
    addressLine,
    addressDetail,
    city,
    state?.abbreviation,
    zip,
    citizenshipStatus,
    formValid,
  ]);

  const setFieldsFromProfile = useCallback((profile: CommonApplicationProfile) => {
    setCitizenshipStatus(profile.citizenship_status ?? null);
    setAddressLine(profile.address_line ?? '');
    setAddressDetails(profile.address_detail ?? '');
    setCity(profile.city ?? '');
    setState(profile.state ?? null);
    setZip(profile.zip ?? '');
    setGpa(profile.current_gpa);
    setDegreeType(profile.degree_type);
    setMajor(profile.major);
    setInstitution(profile.institution);
    setExpectedEnrollmentDate(profile.expected_enrollment_date);
    setExpectedGraduationDate(profile.expected_graduation_date);
  }, [
    setCitizenshipStatus,
    setAddressLine,
    setAddressDetails,
    setCity,
    setState,
    setZip,
    setGpa,
    setDegreeType,
    setMajor,
    setInstitution,
    setExpectedEnrollmentDate,
    setExpectedGraduationDate,
  ]);

  return { submitForm, setFieldsFromProfile };
};

export default useCommonApplicationForm;
